/**
 * Parwaaz backend — demo data.
 *
 * Seeds a staff roster (passphrase `parwaaz-demo` for everyone), ~25 synthetic applications
 * spread across every workflow state with plausible international pilots, and a blocklist
 * entry. Findings are computed by actually calling `runRules`, and every issued card carries a
 * real Ed25519 signature from `DEMO_KEY`. Used by the dev server (`--seed` / `SEED_DEMO=1`) and
 * by the in-browser demo bootstrap.
 */
import { pbkdf2 } from '@noble/hashes/pbkdf2';
import { sha256 } from '@noble/hashes/sha2';
import { utf8ToBytes } from '@noble/hashes/utils';
import {
  buildPayload,
  cardValidity,
  correctionItems,
  photoHash12,
  riskTier,
  runRules,
  seasonYear,
  signPayload,
  todayIso,
  translitForCard,
  type Application,
  type ApplicationState,
  type CardRecord,
  type FileSlot,
  type GliderClass,
  type PilotFields,
  type Role,
  type TenantConfig,
} from '@parwaaz/shared';
import { bytesToBase64Url, type MemoryPlatform } from '../adapters/memory/platform';
import { MemoryStore } from '../adapters/memory/store';
import { DEMO_KEY } from './demo-key';

export interface SeedDemoOpts {
  count?: number;
  today?: string;
}

// 1×1 transparent PNG — a tiny, valid placeholder for every document/photo slot.
const TINY_PNG_B64 =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';

const DEMO_PASSPHRASE = 'parwaaz-demo';

const STAFF: { user: string; role: Role; displayName: string }[] = [
  { user: 'sdm', role: 'sdm', displayName: 'Sub-Divisional Magistrate' },
  { user: 'assistant', role: 'assistant', displayName: 'Dealing Assistant' },
  { user: 'evaluator', role: 'evaluator', displayName: 'Technical Evaluator, Billing' },
  { user: 'dc', role: 'dc', displayName: 'Deputy Commissioner, Kangra' },
  { user: 'responder', role: 'responder', displayName: 'Rescue Coordinator' },
  { user: 'admin', role: 'admin', displayName: 'System Administrator' },
];

function deriveKeyB64url(user: string): Uint8Array {
  const salt = utf8ToBytes(`parwaaz|${user}`);
  return pbkdf2(sha256, utf8ToBytes(DEMO_PASSPHRASE), salt, { c: 600_000, dkLen: 32 });
}

function seedStaff(store: MemoryStore, platform: MemoryPlatform): void {
  for (const s of STAFF) {
    store.upsertStaff({ user: s.user, displayName: s.displayName, role: s.role, active: true });
    const derivedKey = bytesToBase64Url(deriveKeyB64url(s.user));
    store.setStaffSecret(s.user, platform.sha256B64url(derivedKey));
  }
}

interface PilotProfile {
  givenNames: string;
  surname: string;
  nationality: string;
  city: string;
  region: string;
  certBody: string;
  certLevel: string;
  gliderMake: string;
  gliderModel: string;
  gliderClass: GliderClass;
  insProvider: string;
}

const PROFILES: PilotProfile[] = [
  { givenNames: 'Lukas', surname: 'Hoffmann', nationality: 'DEU', city: 'Munich', region: 'Bavaria', certBody: 'DHV', certLevel: 'P2', gliderMake: 'Ozone', gliderModel: 'Rush 6', gliderClass: 'EN-B', insProvider: 'DHV Versicherung' },
  { givenNames: 'Camille', surname: 'Rousseau', nationality: 'FRA', city: 'Annecy', region: 'Haute-Savoie', certBody: 'FFVL', certLevel: 'P3', gliderMake: 'Advance', gliderModel: 'Iota 3', gliderClass: 'EN-B', insProvider: 'Allianz' },
  { givenNames: 'Oliver', surname: 'Bennett', nationality: 'GBR', city: 'Bristol', region: 'England', certBody: 'BHPA', certLevel: 'Pilot', gliderMake: 'Nova', gliderModel: 'Mentor 7', gliderClass: 'EN-B', insProvider: 'World Nomads' },
  { givenNames: 'Elena', surname: 'Baumann', nationality: 'CHE', city: 'Interlaken', region: 'Bern', certBody: 'SHV/FSVL', certLevel: 'P4', gliderMake: 'Gin', gliderModel: 'Bonanza 3', gliderClass: 'EN-C', insProvider: 'Allianz' },
  { givenNames: 'Ananya', surname: 'Sharma', nationality: 'IND', city: 'Manali', region: 'Himachal Pradesh', certBody: 'FAI/IPPI', certLevel: 'IPPI-3', gliderMake: 'Ozone', gliderModel: 'Rush 6', gliderClass: 'EN-B', insProvider: 'Bajaj Allianz' },
  { givenNames: 'Rohan', surname: 'Deshmukh', nationality: 'IND', city: 'Pune', region: 'Maharashtra', certBody: 'FAI/IPPI', certLevel: 'IPPI-4', gliderMake: 'Skywalk', gliderModel: 'Chili 5', gliderClass: 'EN-B', insProvider: 'HDFC Ergo' },
  { givenNames: 'Tenzin', surname: 'Sherpa', nationality: 'NPL', city: 'Pokhara', region: 'Gandaki', certBody: 'APPI', certLevel: 'P3', gliderMake: 'BGD', gliderModel: 'Cure 2', gliderClass: 'EN-C', insProvider: 'Global Rescue' },
  { givenNames: 'Charlotte', surname: 'Nguyen', nationality: 'AUS', city: 'Byron Bay', region: 'New South Wales', certBody: 'APPI', certLevel: 'P4', gliderMake: 'Niviuk', gliderModel: 'Peak 6', gliderClass: 'EN-D', insProvider: 'World Nomads' },
  { givenNames: 'Haruto', surname: 'Sato', nationality: 'JPN', city: 'Hakuba', region: 'Nagano', certBody: 'CIVL', certLevel: 'P4', gliderMake: 'Ozone', gliderModel: 'Zeno 2', gliderClass: 'EN-D', insProvider: 'Global Rescue' },
  { givenNames: 'Meera', surname: 'Krishnan', nationality: 'IND', city: 'Bengaluru', region: 'Karnataka', certBody: 'FAI/IPPI', certLevel: 'IPPI-3', gliderMake: 'Phi', gliderModel: 'Maestro', gliderClass: 'EN-B', insProvider: 'HDFC Ergo' },
  { givenNames: 'Arjun', surname: 'Thapa', nationality: 'IND', city: 'Darjeeling', region: 'West Bengal', certBody: 'FAI/IPPI', certLevel: 'IPPI-2', gliderMake: 'Ozone', gliderModel: 'Rush 6', gliderClass: 'EN-A', insProvider: 'Bajaj Allianz' },
  { givenNames: 'Sophie', surname: 'Weber', nationality: 'DEU', city: 'Freiburg', region: 'Baden-Württemberg', certBody: 'DHV', certLevel: 'P3', gliderMake: 'Advance', gliderModel: 'Iota 3', gliderClass: 'EN-B', insProvider: 'DHV Versicherung' },
  { givenNames: 'Marco', surname: 'Rossi', nationality: 'CHE', city: 'Lugano', region: 'Ticino', certBody: 'SHV/FSVL', certLevel: 'P2', gliderMake: 'Nova', gliderModel: 'Mentor 7', gliderClass: 'EN-B', insProvider: 'Allianz' },
  { givenNames: 'Isla', surname: 'MacDonald', nationality: 'GBR', city: 'Edinburgh', region: 'Scotland', certBody: 'BHPA', certLevel: 'Club Pilot', gliderMake: 'Skywalk', gliderModel: 'Chili 5', gliderClass: 'EN-B', insProvider: 'World Nomads' },
  { givenNames: 'Priya', surname: 'Nair', nationality: 'IND', city: 'Kochi', region: 'Kerala', certBody: 'FAI/IPPI', certLevel: 'IPPI-3', gliderMake: 'Gin', gliderModel: 'Bonanza 3', gliderClass: 'EN-C', insProvider: 'Bajaj Allianz' },
];

function addDays(dateIso: string, days: number): string {
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysAgoIso(today: string, days: number): string {
  const d = new Date(`${today}T09:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function addHours(iso: string, hours: number): string {
  const d = new Date(iso);
  d.setUTCHours(d.getUTCHours() + hours);
  return d.toISOString();
}

interface SeedSpec {
  state: ApplicationState;
  daysAgo: number;
  assignedTo?: string;
  redInsurance?: boolean;
  enDWarning?: boolean;
  printed?: boolean;
  revoke?: boolean;
}

const SPECS: SeedSpec[] = [
  { state: 'Submitted', daysAgo: 1 },
  { state: 'Submitted', daysAgo: 2, redInsurance: true },
  { state: 'Validated', daysAgo: 3 },
  { state: 'CorrectionRequested', daysAgo: 4 },
  { state: 'CorrectionRequested', daysAgo: 5, enDWarning: true },
  { state: 'ReadyForEvaluation', daysAgo: 6 },
  { state: 'ReadyForEvaluation', daysAgo: 6, assignedTo: 'evaluator' },
  { state: 'Evaluated', daysAgo: 7 },
  { state: 'Evaluated', daysAgo: 8 },
  { state: 'EvaluationFailed', daysAgo: 8 },
  { state: 'UnderReview', daysAgo: 9 },
  { state: 'UnderReview', daysAgo: 9, assignedTo: 'sdm' },
  { state: 'Approved', daysAgo: 10 },
  { state: 'Approved', daysAgo: 10 },
  { state: 'Approved', daysAgo: 11 },
  { state: 'CardIssued', daysAgo: 12 },
  { state: 'CardIssued', daysAgo: 13 },
  { state: 'CardIssued', daysAgo: 14 },
  { state: 'CardIssued', daysAgo: 15 },
  { state: 'CardIssued', daysAgo: 16, printed: true },
  { state: 'CardIssued', daysAgo: 17, revoke: true },
  { state: 'Rejected', daysAgo: 18 },
  { state: 'Rejected', daysAgo: 19 },
  { state: 'Expired', daysAgo: 20 },
  { state: 'CardIssued', daysAgo: 21 },
];

function makeFields(p: PilotProfile, spec: SeedSpec, today: string, i: number, tenant: TenantConfig): PilotFields {
  const isForeign = p.nationality !== 'IND';
  const insValidTo = spec.redInsurance ? addDays(today, -10) : addDays(today, 180);
  const gliderClass: GliderClass = spec.enDWarning ? 'EN-D' : p.gliderClass;
  const certLevel = spec.enDWarning ? 'IPPI-3' : p.certLevel;
  const fullName = `${p.givenNames} ${p.surname}`;
  // E.164, no separators, so rules.ts's phone.format rule (`^\+[1-9]\d{6,14}$`) passes.
  const phone = isForeign ? '+41791234567' : '+919876543210';
  return {
    givenNames: p.givenNames, surname: p.surname, fathersName: `${p.surname} Senior`,
    addressLine: `${100 + i} Skyline Road`, city: p.city, region: p.region, country: p.nationality, postcode: '000000',
    nationality: p.nationality, dob: '1990-05-15', phone,
    email: `${p.givenNames.toLowerCase()}.${p.surname.toLowerCase()}@example.com`,
    docType: isForeign ? 'passport' : 'aadhaar', docNumber: `DOC${1000 + i}`, docIssuingCountry: p.nationality,
    docExpiry: addDays(today, 900), mrzLine1: '', mrzLine2: '',
    // Visa/arrival dates bracket the whole season so visa.covers passes regardless of `today`.
    isForeign, arrivalDate: isForeign ? addDays(tenant.season.validFrom, -10) : '', visaType: isForeign ? 'Tourist' : '',
    visaValidFrom: isForeign ? addDays(tenant.season.validFrom, -10) : '', visaValidTo: isForeign ? addDays(tenant.season.validTo, 30) : '',
    certBody: p.certBody, certLevel, certNumber: `${p.certBody}-${2000 + i}`, certIssued: addDays(today, -400),
    certExpiry: addDays(today, 400), certHolderName: fullName,
    gliderMake: p.gliderMake, gliderModel: p.gliderModel, gliderSize: 'M', gliderClass,
    reserveMake: 'Companion', reserveModel: 'Steerable', reserveRepack: addDays(today, -60),
    insProvider: p.insProvider, insPolicyNo: `POL-${3000 + i}`, insInsuredName: fullName,
    insCoverText: 'This policy covers paragliding, hang gliding and air sports activities.', insValidTo,
    medicalValidTo: addDays(today, 200),
    emgName: 'Contact Person', emgRelationship: 'Spouse', emgPhone: '+14155550100', bloodGroup: 'O+',
    declarationAccepted: [true, true, true, true], bondAccepted: [true, true, true, true, true, true, true],
    typedName: fullName, consentPurpose: true,
  };
}

function issueCard(store: MemoryStore, tenant: TenantConfig, app: Application, issuedAt: string): CardRecord {
  const cardNo = store.allocateCardNo(tenant.season.cardPrefix, seasonYear(tenant));
  const validity = cardValidity(tenant, issuedAt.slice(0, 10));
  const name = translitForCard(app.fields.surname, app.fields.givenNames);
  const hash12 = photoHash12(app.photoSha256!);
  const payload = buildPayload({
    cardNo, name, nationality: app.fields.nationality, validFrom: validity.validFrom, validTo: validity.validTo,
    cert: app.fields.certLevel, photoHash12: hash12, keyId: DEMO_KEY.kid,
  });
  const signature = signPayload(payload, DEMO_KEY.seedB64url);
  const card: CardRecord = { cardNo, validFrom: validity.validFrom, validTo: validity.validTo, keyId: DEMO_KEY.kid, payload, signature, photoHash12: hash12, issuedAt };
  store.upsertCard({ ...card, ref: app.ref });
  return card;
}

function buildHistory(store: MemoryStore, tenant: TenantConfig, app: Application, spec: SeedSpec, submittedAt: string, today: string): void {
  const t = (h: number) => addHours(submittedAt, h);
  const audit = (at: string, actor: string, role: Role | 'system', action: string, from: string, to: string, reason: string) =>
    store.appendAudit({ at, actor, role, action, entity: 'application', entityId: app.ref, fromState: from, toState: to, reason });

  audit(submittedAt, 'pilot', 'pilot', 'submit', '', 'Submitted', 'application submitted');

  if (app.riskTier === 'red') {
    app.correctionRequest = { items: correctionItems(app.findings), message: 'Automatically generated from validation findings.', requestedBy: 'system', at: submittedAt };
    audit(submittedAt, 'system', 'system', 'validate', 'Submitted', 'Submitted', 'red tier: correction required');
    if (spec.state === 'Submitted') return;
  } else {
    app.status = 'ReadyForEvaluation';
    audit(t(1), 'system', 'system', 'validate', 'Submitted', 'ReadyForEvaluation', 'validated, queued for evaluation');
    if (spec.state === 'Submitted') return;
  }

  if (spec.state === 'Validated') {
    app.status = 'Validated';
    return;
  }
  if (spec.state === 'CorrectionRequested') {
    app.status = 'CorrectionRequested';
    app.correctionRequest = { items: ['Please re-upload a clearer insurance document.'], message: 'Insurance document illegible.', requestedBy: 'assistant', at: t(4) };
    audit(t(4), 'assistant', 'assistant', 'requestCorrection', 'ReadyForEvaluation', 'CorrectionRequested', 'correction requested');
    return;
  }
  if (spec.state === 'ReadyForEvaluation') return;

  const outcome = spec.state === 'EvaluationFailed' ? 'fail' : app.riskTier === 'amber' ? 'conditional' : 'pass';
  app.status = outcome === 'fail' ? 'EvaluationFailed' : 'Evaluated';
  app.evaluatorUser = 'evaluator';
  app.assessmentOutcome = outcome;
  app.assessedAt = t(24);
  app.assessmentChecklist = { documentsComplete: true, certificationValid: outcome !== 'fail', insuranceValid: app.riskTier !== 'red' };
  audit(t(24), 'evaluator', 'evaluator', 'evaluate', 'ReadyForEvaluation', app.status, `evaluated: ${outcome}`);
  if (spec.state === 'EvaluationFailed' || spec.state === 'Evaluated') return;

  app.status = 'UnderReview';
  app.reviewerUser = 'assistant';
  app.recommendation = spec.state === 'Rejected' ? 'reject' : 'approve';
  app.reviewedAt = t(30);
  audit(t(30), 'assistant', 'assistant', 'recommend', 'Evaluated', 'UnderReview', `recommended: ${app.recommendation}`);
  if (spec.state === 'UnderReview') return;

  if (spec.state === 'Rejected') {
    app.status = 'Rejected';
    app.approverUser = 'sdm';
    app.decision = 'rejected';
    app.decisionReason = 'Insurance cover does not name paragliding as a covered activity.';
    app.decidedAt = t(36);
    audit(t(36), 'sdm', 'sdm', 'reject', 'UnderReview', 'Rejected', app.decisionReason);
    return;
  }

  app.status = 'Approved';
  app.approverUser = 'sdm';
  app.decision = 'approved';
  app.decidedAt = t(36);
  audit(t(36), 'sdm', 'sdm', 'approve', 'UnderReview', 'Approved', 'approved');
  if (spec.state === 'Approved') return;

  const issuedAt = t(40);
  const card = issueCard(store, tenant, app, issuedAt);
  app.card = card;
  app.status = 'CardIssued';
  audit(issuedAt, 'sdm', 'sdm', 'allocateCard', 'Approved', 'Approved', `card ${card.cardNo} allocated`);
  audit(issuedAt, 'sdm', 'sdm', 'attachSignature', 'Approved', 'CardIssued', `card ${card.cardNo} issued`);

  if (spec.printed) {
    app.card = { ...card, printedAt: t(41), printSheet: 'sheet-01' };
    audit(t(41), 'assistant', 'assistant', 'markPrinted', 'CardIssued', 'CardIssued', 'marked printed');
  }
  if (spec.revoke) {
    const revokedAt = t(48);
    const revocationReason = 'Reported flying outside the designated launch site without authorisation.';
    app.card = { ...app.card!, revokedAt, revocationReason };
    app.status = 'Revoked';
    store.upsertCard({ ...app.card, ref: app.ref });
    audit(revokedAt, 'sdm', 'sdm', 'revoke', 'CardIssued', 'Revoked', revocationReason);
  }
  if (spec.state === 'Expired') {
    app.card = { ...app.card!, validTo: addDays(today, -30) };
    app.status = 'Expired';
    store.upsertCard({ ...app.card, ref: app.ref });
  }
}

function seedOne(store: MemoryStore, tenant: TenantConfig, platform: MemoryPlatform, p: PilotProfile, spec: SeedSpec, today: string, i: number): void {
  const submittedAt = daysAgoIso(today, spec.daysAgo);
  const fields = makeFields(p, spec, today, i, tenant);
  const findings = runRules(fields, null, { today, tenant, duplicates: { activeCardForDoc: false, onBlocklist: false } });
  const tier = riskTier(findings);
  const ref = `A-${tenant.season.key}-demo${String(i).padStart(4, '0')}`;
  const statusToken = platform.randomToken(24);

  const files: Partial<Record<FileSlot, string>> = {
    doc: store.putFile(ref, 'doc', 'document.png', 'image/png', TINY_PNG_B64),
    photo: store.putFile(ref, 'photo', 'photo.png', 'image/png', TINY_PNG_B64),
    cert: store.putFile(ref, 'cert', 'certificate.png', 'image/png', TINY_PNG_B64),
    ins: store.putFile(ref, 'ins', 'insurance.png', 'image/png', TINY_PNG_B64),
  };

  const app: Application = {
    ref, status: 'Submitted', season: tenant.season.key, createdAt: submittedAt, updatedAt: submittedAt,
    idemKey: `demo-${i}`, statusTokenHash: platform.sha256B64url(statusToken),
    fields, mrz: null, findings, riskTier: tier, rulesetVersion: tenant.rules.rulesetVersion,
    assignedTo: spec.assignedTo, notes: [], correctionRequest: null, card: null, files,
    photoSha256: platform.sha256(TINY_PNG_B64),
  };

  buildHistory(store, tenant, app, spec, submittedAt, today);
  app.updatedAt = store.listAudit(ref).at(-1)?.at ?? submittedAt;
  store.appendApplication(app);
}

function seedBlocklist(store: MemoryStore): void {
  store.snapshot().blocklist.push({ docNumber: 'DOC9999', reason: 'Reported for unsafe flying without registration, 2025 season.' });
}

/** Seeds the staff roster, config and ~25 synthetic applications spread across every state. */
export function seedDemo(store: MemoryStore, tenant: TenantConfig, platform: MemoryPlatform, opts: SeedDemoOpts = {}): void {
  const today = opts.today ?? todayIso();
  seedStaff(store, platform);
  store.setConfig('season', tenant.season.key);
  store.setConfig('key_id', DEMO_KEY.kid);
  store.setConfig('revocation_version', '0');
  store.setConfig('submit_window_minutes', '10');
  store.setConfig('submit_window_max', '60');
  seedBlocklist(store);

  const count = Math.min(opts.count ?? SPECS.length, SPECS.length);
  for (let i = 0; i < count; i += 1) {
    seedOne(store, tenant, platform, PROFILES[i % PROFILES.length]!, SPECS[i]!, today, i);
  }
  store.flush();
}
